import type {
  ReleaseArtifactRow,
  ReleaseAppRow,
  ReleaseAppVersionRow,
  ReleaseChannelRow,
  RuntimeConfigRow
} from '../api';

export function initialsFrom(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return '?';
  }

  return trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();
}

export function formatRelativeDate(value?: string | null) {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  const diffMs = Date.now() - parsed.getTime();
  const isFuture = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);

  const diffMinutes = Math.max(1, Math.round(absDiffMs / 60000));
  if (diffMinutes < 60) {
    return isFuture ? `in ${diffMinutes}m` : `${diffMinutes}m ago`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return isFuture ? `in ${diffHours}h` : `${diffHours}h ago`;
  }

  const diffDays = Math.round(diffHours / 24);
  return isFuture ? `in ${diffDays}d` : `${diffDays}d ago`;
}

export function bumpVersion(version: string) {
  const parts = version
    .split('.')
    .map((part) => Number.parseInt(part, 10));

  if (parts.length < 3 || parts.some((part) => Number.isNaN(part))) {
    return version;
  }

  parts[2] += 1;
  return parts.slice(0, 3).join('.');
}

export function formatAbsoluteDate(value?: string | null) {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short'
  });
}

export function toneFromBoolean(value: boolean): 'success' | 'warn' | 'neutral' {
  return value ? 'success' : 'warn';
}

export function formatArtifactSummary(artifact?: ReleaseArtifactRow | null) {
  if (!artifact) {
    return '—';
  }

  const source = artifact.gitTag?.trim()
    ? `Tag ${artifact.gitTag.trim()}`
    : artifact.gitBranch?.trim() || 'CI bundle';
  const builtAt =
    artifact.buildGeneratedAt || artifact.created || artifact.updated;

  return builtAt ? `${source} · ${formatArtifactBuildDate(builtAt)}` : source;
}

export function formatArtifactBuildDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return `${parsed.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  })} UTC`;
}

export function getArtifactTechnicalId(
  artifact?: ReleaseArtifactRow | null
) {
  if (!artifact) {
    return '';
  }

  return (
    artifact.buildId?.trim() ||
    artifact.gitSha?.trim() ||
    artifact.bundleSha256.trim()
  );
}

export function formatChannelBundleHostSummary(channel?: ReleaseChannelRow | null) {
  if (!channel) {
    return '—';
  }

  return channel.bundleCdnHostOverride || 'Default';
}

export function formatChannelLabel(channel?: ReleaseChannelRow | null) {
  if (!channel) {
    return '—';
  }

  return `${channel.environment}/${channel.channel}`;
}

export function formatReleaseAppVersionLabel(
  version: ReleaseAppVersionRow | null | undefined,
  releaseAppById: Map<string, ReleaseAppRow>
) {
  if (!version) {
    return '—';
  }

  const app = releaseAppById.get(version.releaseAppId);
  const appName = app?.name || 'App';
  const platform = app?.platform ? ` (${app.platform})` : '';
  return `${appName}${platform} · ${version.version}`;
}

export function resolveRuntimeConfigChannel(
  config: RuntimeConfigRow,
  channels: ReleaseChannelRow[]
) {
  const selectedById = config.otaChannelId
    ? channels.find((channel) => channel.releaseChannelId === config.otaChannelId)
    : undefined;

  if (selectedById) {
    return selectedById;
  }

  if (!config.otaChannel) {
    return undefined;
  }

  // otaChannel may be stored as "env/channel" or just "channel"
  const rawOtaChannel = config.otaChannel || '';
  const slashIdx = rawOtaChannel.indexOf('/');
  const channelEnv = slashIdx !== -1 ? rawOtaChannel.slice(0, slashIdx) : null;
  const channelName = slashIdx !== -1 ? rawOtaChannel.slice(slashIdx + 1) : rawOtaChannel;

  return channels.find(
    (channel) =>
      channel.organizationId === config.organizationId &&
      (channel.projectId || '') === (config.projectId || '') &&
      channel.channel === channelName &&
      (channelEnv ? channel.environment === channelEnv : channel.environment === config.environment)
  );
}

export function formatRuntimeConfigChannel(
  config: RuntimeConfigRow,
  channels: ReleaseChannelRow[]
) {
  const channel = resolveRuntimeConfigChannel(config, channels);

  if (channel) {
    return formatChannelLabel(channel);
  }

  if (config.otaChannelId) {
    return config.otaChannel || 'unresolved target';
  }

  return config.otaChannel || 'default target';
}
